import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { IoMdPrint } from "react-icons/io";
import { FaFileExcel } from "react-icons/fa";
import { toast } from "react-toastify";
import userResources from "api/userResources";
import Select from "react-tailwindcss-select";

export default function PaxPayment() {
  const [isLoading, setIsLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState(1);
  const [selectedOption, setSelectedOption] = useState({
    value: 1,
    label: "Client Pay",
  });
  const paymentOptions = [
    { value: null, label: "All" },
    { value: 1, label: "Client Pay" }, // Client has to pay us
    { value: 0, label: "Settled" }, // No one owes anything
    { value: -1, label: "We Pay Client" }, // We owe money to client
  ];
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0,
    lastPage: 0,
  });

  const handleChange = (value) => {
    setSelectedOption(value);
    setPendingPaymentStatus(value?.value);
  };

  // --- Excel Export Handler ---
  const handleExportExcel = () => {
    const excelData = invoices.map((inv) => ({
      "Invoice No.": inv.invoice_number || "-",
      "Full Name": inv.customer?.full_name || "-",
      Departure: inv.fromAirport?.iata || "-",
      Destination: inv.toAirport?.iata || "-",
      Airline: inv.airLine?.airline_name || "-",
      "Pending Amount": parseFloat(inv.airticketFromPaxPendingPayment ?? 0),
    }));

    // Calculate total of "Pending Amount"
    const totalPending = excelData.reduce(
      (sum, row) => sum + (parseFloat(row["Pending Amount"]) || 0),
      0
    );

    const totalRow = {
      "Invoice No.": "",
      "Full Name": "",
      Departure: "",
      Destination: "",
      Airline: "Total",
      "Pending Amount": totalPending,
    };

    const fullData = [...excelData, totalRow];

    const title = "Passenger Pending Payment Report";
    const titleRow = [[title]];
    const blankRow = [[]];
    const columns = Object.keys(fullData[0]);

    const worksheet = XLSX.utils.aoa_to_sheet([...titleRow, ...blankRow]);
    const jsonSheet = XLSX.utils.json_to_sheet(fullData, { origin: 2 });
    Object.entries(jsonSheet).forEach(([key, value]) => {
      worksheet[key] = value;
    });

    // Merge title across columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: columns.length - 1 },
      },
    ];

    // Style title
    worksheet["A1"].s = {
      font: { sz: 16, bold: true, color: { rgb: "FF1F497D" } },
      alignment: { horizontal: "center" },
      fill: { fgColor: { rgb: "FFEAF1FB" } },
    };

    // Style header row
    const headerRow = 2;
    for (let c = 0; c < columns.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: headerRow, c });
      const cell = worksheet[addr];
      if (!cell) continue;
      cell.s = {
        font: { bold: true, color: { rgb: "FFFFFFFF" } },
        fill: { fgColor: { rgb: "FF4F81BD" } },
        alignment: { horizontal: "center" },
        border: { bottom: { style: "thin", color: { rgb: "FF000000" } } },
      };
    }

    // Stripe data rows
    const dataStart = headerRow + 1;
    const dataEnd = dataStart + fullData.length - 1;
    for (let r = dataStart; r <= dataEnd; r++) {
      const isEven = (r - dataStart) % 2 === 0;
      for (let c = 0; c < columns.length; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = worksheet[addr];
        if (!cell) continue;

        // Highlight total row
        if (r === dataEnd) {
          cell.s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "FFE6E6E6" } },
          };
        } else {
          cell.s = {
            fill: {
              fgColor: { rgb: isEven ? "FFFFFFFF" : "FFF3F3F3" },
            },
          };
        }
      }
    }

    // Format "Pending Amount" as number
    const pendingColIndex = columns.indexOf("Pending Amount");
    for (let r = dataStart; r <= dataEnd; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: pendingColIndex });
      const cell = worksheet[addr];
      if (!cell) continue;
      cell.t = "n";
      cell.z = "#,##0.00";
    }

    worksheet["!cols"] = columns.map((col) => ({
      wch: col === "Pending Amount" ? 18 : 20,
    }));

    worksheet["!freeze"] = { xSplit: 0, ySplit: headerRow + 1 };
    worksheet["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRow, c: 0 },
        e: { r: headerRow, c: columns.length - 1 },
      }),
    };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PassengerPayments");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(
      blob,
      `Passenger_Payment_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // --- Data Fetching ---
  useEffect(() => {
    const getInvoiceList = async () => {
      setIsLoading(true);
      try {
        const params = {
          limit: pagination.pageSize,
          page: pagination.pageIndex + 1,
          balance_status_pax: pendingPaymentStatus,
        };
        const response = await userResources.paxPendingPayment(params);
        const data = response?.data;

        setInvoices(data?.data || []);
        if (data?.paginate_data) {
          const { current_page, per_page, total, last_page } =
            data.paginate_data;
          setPagination((prev) => ({
            ...prev,
            pageIndex: current_page - 1,
            pageSize: per_page,
            totalRows: total,
            lastPage: last_page,
          }));
        }
      } catch (error) {
        console.error("Error fetching invoice list:", error);
        setInvoices([]);
        toast.error("Error fetching invoice list");
      } finally {
        setIsLoading(false);
      }
    };

    getInvoiceList();
  }, [pagination.pageIndex, pagination.pageSize, pendingPaymentStatus]);

  // --- Pagination Controls ---
  const handlePageChange = (newPageIndex) => {
    if (newPageIndex < 0 || newPageIndex >= pagination.lastPage) return;
    setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
  };
  const disablePrev = pagination.pageIndex === 0;
  const disableNext = pagination.pageIndex + 1 >= pagination.lastPage;

  // --- Print Handler ---
  const handlePrint = () => window.print();

  return (
    <section>
      {/* Print & Excel Icons */}
      <div className="flex justify-between pt-2">
        <div className="w-52">
          <Select
            value={selectedOption}
            onChange={handleChange}
            options={paymentOptions}
            primaryColor={"blue"}
          />
        </div>
        <div className="flex gap-2">
          <FaFileExcel
            onClick={handleExportExcel}
            size={25}
            className="ml-4 cursor-pointer text-green-600"
          />
          <IoMdPrint
            onClick={handlePrint}
            size={25}
            className="cursor-pointer text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Print Styles */}
      <style>
        {`@page {
            margin: 0.5cm;
        }
        @media print {
          html, body { overflow: visible !important; }
          body * { visibility: hidden; }
          .printable, .printable * { visibility: visible !important; color: black !important; background: none !important; box-shadow: none !important; }
          .printable { position: absolute; top: 0; left: 0; width: 100%; }
          .printable table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          .printable th, .printable td { border: 1px solid #000; padding: 2px; text-align: left; }
          tfoot { display: table-row-group; }
        }`}
      </style>

      {/* Invoice Table */}
      <div className="printable overflow-x-auto py-2">
        <table className="min-w-full table-fixed divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-800">
            <tr>
              <th className="w-24 px-2 py-4 text-left text-xs font-medium uppercase text-white">
                Invoice No.
              </th>
              <th className="w-1/3 px-2 py-4 text-left text-xs font-medium uppercase text-white">
                Last Name/First Name
              </th>
              <th className="w-12 px-2 py-4 text-left text-xs font-medium uppercase text-white">
                Dep
              </th>
              <th className="w-12 px-2 py-4 text-left text-xs font-medium uppercase text-white">
                Des
              </th>
              <th className="w-32 px-2 py-4 text-left text-xs font-medium uppercase text-white">
                Airline
              </th>
              <th className="w-12 px-2 py-4 text-left text-xs font-medium uppercase text-white">
                Pending Amount
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600 bg-gray-700">
            {isLoading ? (
              <tr className="h-[450px]">
                <td colSpan="6" className="py-6 text-center">
                  <span className="loading loading-spinner loading-lg text-white"></span>
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr className="h-[450px]">
                <td colSpan="6" className="py-6 text-center">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <p className="text-lg text-white">No Data Available</p>
                  </div>
                </td>
              </tr>
            ) : (
              invoices.map((inv) => (
                <tr key={inv.id} className="bg-gray-700">
                  <td className="px-2 py-3 text-sm text-black">
                    {inv.invoice_number || "-"}
                  </td>
                  <td className="px-2 py-3 text-sm text-black">
                    {inv.customer?.full_name || "-"}
                  </td>
                  <td className="px-2 py-3 text-sm text-black">
                    {inv.fromAirport?.iata || "-"}
                  </td>
                  <td className="px-2 py-3 text-sm text-black">
                    {inv.toAirport?.iata || "-"}
                  </td>
                  <td className="truncate px-2 py-3 text-sm text-black">
                    {inv.airLine?.airline_name || "-"}
                  </td>
                  <td className="px-2 py-3 text-sm text-black">
                    {inv.airticketFromPaxPendingPayment}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center space-x-2">
          <label htmlFor="pageSize" className="text-gray-800 dark:text-white">
            Per Page:
          </label>
          <select
            id="pageSize"
            value={pagination.pageSize}
            onChange={(e) =>
              setPagination((prev) => ({
                ...prev,
                pageSize: Number(e.target.value),
                pageIndex: 0,
              }))
            }
            className="rounded border border-gray-300 bg-white p-1 text-gray-800"
          >
            {[10, 50, 100, 500, 1000].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => handlePageChange(pagination.pageIndex - 1)}
            disabled={disablePrev}
            className={`rounded px-4 py-2 text-white ${
              disablePrev
                ? "cursor-not-allowed bg-gray-400"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            Previous
          </button>
          <span className="text-gray-800 dark:text-white">
            Page {pagination.pageIndex + 1} of {pagination.lastPage}
          </span>
          <button
            onClick={() => handlePageChange(pagination.pageIndex + 1)}
            disabled={disableNext}
            className={`rounded px-4 py-2 text-white ${
              disableNext
                ? "cursor-not-allowed bg-gray-400"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </section>
  );
}
